import React, { useMemo, useState } from 'react';
import MuiMultiMaxSelectionDropDown from '@shared/components/MuiMultiMaxSelectionDropDown';
import SearchControlOHT from '@shared/components/SearchControlOHT';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import { AnimatePresence } from 'framer-motion';
import MotionSection from '@shared/components/animations/MotionSection';
import { debounce, range, sortBy } from 'lodash';
import { useQuery } from '@apollo/client';
import ApolloError from '@shared/components/ApolloError';
import VIEW_SELECT_OPTIONS from '../constants/viewSelectOptions';
import GET_MAXIMUM_INSTANCE from '../queries/getMaximumInstanceQuery';
import ViewSelector from './selections/ViewSelector';
import SACBaselineView from './views/baselineView/SACBaselineView';
import SACDeltaView from './views/deltaView/SACDeltaView';
import SACTotalView from './views/totalView/SACTotalView';
import PageInstructions from '@shared/components/PageInstructions';
import { REPORT_FORMAT_TYPES } from '@shared/constants/reportFormatTypes';
import { DATA_DOWNLOAD_TYPES } from '@shared/constants/DataDownloadTypes';
import DownloadModalButtonWithHandler from '@shared/components/DownloadModalButtonWithHandler';

const DEBOUNCE_DELAY = 500;

const REPORT_KEYS = {
  BASELINE: 'baseline',
  DELTA: 'delta',
  TOTAL: 'total'
};

const REQUIRED_DELTA_INSTANCE_COUNT = 2;

/**
 * @typedef {object} StrengthsAndChallengesProps
 * @property {number} [orgId]
 * @property {string} [title]
 * @property {string} [description]
 */

/**
 * @param {StrengthsAndChallengesProps} props
 */
const StrengthsAndChallenges = props => {
  const { title, description, orgId } = props;

  const defaultDeltaInstances = getDefaultDeltaInstanceOptions();

  const [selectedView, setSelectedView] = useState(VIEW_SELECT_OPTIONS.BASELINE.value);
  const [ohts, setOhts] = useState([]);
  const [deltaInstances, setDeltaInstances] = useState(defaultDeltaInstances);
  const [selectedDeltaInstancesPair, setSelectedDeltaInstancesPair] =
    useState(defaultDeltaInstances);

  const debouncedSetOhts = useMemo(
    () =>
      debounce(newOhts => {
        setOhts(newOhts);
      }, DEBOUNCE_DELAY),
    [setOhts]
  );

  const {
    data: maximumInstanceData,
    loading: getMaximumInstanceLoading,
    error: getMaximumInstanceError
  } = useQuery(GET_MAXIMUM_INSTANCE, {
    variables: {
      orgId: orgId
    }
  });

  const deltaInstanceOptions = getDeltaInstanceOptions(maximumInstanceData?.maximumInstance);

  const handleViewSelected = value => {
    setSelectedView(value);
    setDefaultDeltaOptionSelection(value);
  };

  const setDeltaInstancesInternal = deltaInstances => {
    setDeltaInstances(deltaInstances);

    if (deltaInstances.length !== REQUIRED_DELTA_INSTANCE_COUNT) {
      return;
    }

    const sortedInstances = sortBy(deltaInstances, x => x.value);
    setSelectedDeltaInstancesPair(sortedInstances);
  };

  const setDefaultDeltaOptionSelection = value => {
    const isDeltaView = value === VIEW_SELECT_OPTIONS.DELTA.value;

    if (!isDeltaView) {
      setDeltaInstancesInternal([]);
      return;
    }

    setDeltaInstancesInternal(getDefaultDeltaInstanceOptions());
  };

  if (getMaximumInstanceError) {
    return <ApolloError />;
  }

  const isDeltaSelectionDisabled = maximumInstanceData?.maximumInstance < 2;
  const reportOptions = getReportOptions(isDeltaSelectionDisabled);

  return (
    <>
      <div className='strength-and-challenges-header'>
        <PageInstructions title={title} description={description} />
        <DownloadModalButtonWithHandler
          reports={reportOptions}
          dataDownloadType={DATA_DOWNLOAD_TYPES.strengthsAndChallenges}
          reportFormatOptions={[REPORT_FORMAT_TYPES.CSV, REPORT_FORMAT_TYPES.PDF]}
          downloadFilters={{
            ohts,
            orgId,
            deltaInstances: selectedDeltaInstancesPair
          }}
        />
      </div>

      <div className='strength-and-challenges-filters'>
        <ViewSelector
          options={reportOptions}
          selectedView={selectedView}
          setSelectedView={handleViewSelected}
        />
        <div className='dropdown-box'>
          <p className='bold'>OHT(s)</p>
          <SearchControlOHT
            selectedSearchOHTs={ohts}
            setSearchOHT={debouncedSetOhts}
            inputLabel='All'
            limitTags={3}
          />
        </div>
        <div className='dropdown-box'>
          <p className='bold'>Delta Instance Selector</p>
          <SkeletonWrapper show={getMaximumInstanceLoading} inheritParentSize>
            <MuiMultiMaxSelectionDropDown
              id='select-delta-instance'
              data-testid='select-delta-instance'
              inputLabel='Provide'
              options={deltaInstanceOptions}
              enabled={selectedView === VIEW_SELECT_OPTIONS.DELTA.value}
              setSelectedValues={setDeltaInstancesInternal}
              selectedValues={
                selectedView === VIEW_SELECT_OPTIONS.DELTA.value ? deltaInstances : []
              }
              maxSelection={2}
            />
          </SkeletonWrapper>
        </div>
      </div>

      <AnimatePresence mode='wait'>
        {selectedView === VIEW_SELECT_OPTIONS.BASELINE.value && (
          <MotionSection keyProp={REPORT_KEYS.BASELINE}>
            <SACBaselineView ohts={ohts} orgId={orgId} />
          </MotionSection>
        )}
        {selectedView === VIEW_SELECT_OPTIONS.DELTA.value && (
          <MotionSection keyProp={REPORT_KEYS.DELTA}>
            <SACDeltaView deltaInstances={selectedDeltaInstancesPair} ohts={ohts} orgId={orgId} />
          </MotionSection>
        )}
        {selectedView === VIEW_SELECT_OPTIONS.TOTAL.value && (
          <MotionSection keyProp={REPORT_KEYS.TOTAL}>
            <SACTotalView ohts={ohts} orgId={orgId} />
          </MotionSection>
        )}
      </AnimatePresence>
    </>
  );
};

const getDefaultDeltaInstanceOptions = () => getDeltaInstanceOptions(REQUIRED_DELTA_INSTANCE_COUNT);

const getReportOptions = isDeltaSelectionDisabled =>
  Object.values(VIEW_SELECT_OPTIONS).map(item => ({
    ...item,
    disabled: item.value === VIEW_SELECT_OPTIONS.DELTA.value && isDeltaSelectionDisabled
  }));

const getDeltaInstanceOptions = (maximumInstance = 0) =>
  range(1, maximumInstance + 1).map(i => ({
    value: i,
    label: `Instance ${i}`,
    displayName: `Instance ${i}`
  }));

export default StrengthsAndChallenges;
